import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getApplicationsCurrent,
  selectApplications,
} from "../../models/applications/applicationsSlice";
import { useRequestStatus } from "../../models/asyncRequestStatusReducer";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { formatMoney, formatDate } from "../../utils/helpers";
import { LocalStorage } from "../../utils/localStorage";
import { selectMyUser } from "../../models/users/myUserSlice";
import { GlobalFlashMessage } from "../../components/flashMessage";
import { Spinner } from "../../components/spinner";
import { capitalizeFirstLetter } from "../../utils/formatting";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  ModuleRegistry,
  AllCommunityModule,
  ICellRendererParams,
  ValueGetterParams,
  FirstDataRenderedEvent,
  RowClickedEvent,
  themeQuartz,
  StateUpdatedEvent,
} from "ag-grid-community";
import { Alert, Tooltip } from "antd";
import { WarningFilled } from "@ant-design/icons";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

// Custom theme with your design system colors
const customTheme = themeQuartz.withParams({
  // Header styling - dark background
  headerBackgroundColor: "#262626",
  headerTextColor: "#ffffff",

  // Grid background and alternating rows
  backgroundColor: "#ffffff",
  oddRowBackgroundColor: "#f9f9f9",

  // Row hover effect - light teal with transparency
  rowHoverColor: "rgba(82, 193, 178, 0.15)",

  // Border radius
  borderRadius: 8,

  // Primary/accent color (for focus states, selections, filters, sort indicators)
  accentColor: "#52c1b2",
});

interface FilterableDashboardProps {
  search: string;
  activeFilter: {
    assignedToMe?: boolean;
    decisionStatus?: string;
  };
  dashboardFilter?: string; // Custom filter from dashboard drilldowns
  includeArchived: boolean;
  setClearColumnFiltersHandler: (handler: () => void) => void;
  setExportCsvHandler: (handler: () => void) => void;
  fiSlug?: string; // FI slug for generating user-specific storage keys
  userId?: number; // User ID for generating user-specific storage keys
}

// Responsive breakpoint - matches Bootstrap's sm breakpoint
const MOBILE_BREAKPOINT = 767;
const MOBILE_GRID_MIN_WIDTH = 600;

// Cell renderer component for archive icon
const ArchiveIconRenderer = (props: ICellRendererParams) => {
  if (props.data?.isArchived === true) {
    return <span className="oi oi-box"></span>;
  }
  return null;
};

// Column id for the booking error indicator, referenced when reconciling a
// column order saved before this column existed.
const BOOKING_ERROR_COL_ID = "hasBookingError";

// Canonical booking-error red, matching the "BOOKING ERROR" badge on the
// application detail sidebar.
const BOOKING_ERROR_RED = "#d32f2f";

const BOOKING_ERROR_LABEL =
  "Booking error - this application failed to book to the Core/LOS";

// Cell renderer for the booking error indicator: a warning triangle in the
// leftmost column means the application failed to book to the Core/LOS - the
// same condition that renders the "BOOKING ERROR" badge on the application
// detail sidebar.
//
// A triangle rather than a dot: the adjacent activity column already uses a
// round dot, and two dots differing only in color would be indistinguishable
// to a red-green colorblind user. Shape carries the meaning alongside color
// (WCAG 1.4.1), and the icon's aria-label gives a screen reader the same
// explanation the tooltip gives everyone else. The label goes on the icon
// rather than the wrapper because antd already renders the icon as
// role="img" - labelling both would announce the sentence, then "warning".
// The bare wrapper span stays because Tooltip needs a ref target.
const BookingErrorRenderer = (props: ICellRendererParams) => {
  if (props.data?.hasBookingError !== true) {
    return null;
  }
  return (
    <Tooltip title={BOOKING_ERROR_LABEL} placement="right">
      <span>
        <WarningFilled aria-label={BOOKING_ERROR_LABEL} />
      </span>
    </Tooltip>
  );
};

// Column order is persisted to localStorage, so users who used the grid before
// the booking error column existed have a saved order without it. Put it back
// at the far left rather than letting ag-grid decide where an unknown column
// belongs.
const withBookingErrorColumn = (orderedColIds: string[]): string[] => {
  if (orderedColIds.includes(BOOKING_ERROR_COL_ID)) {
    return orderedColIds;
  }
  return [BOOKING_ERROR_COL_ID, ...orderedColIds];
};

export const FilterableDashboard = ({
  search,
  activeFilter,
  dashboardFilter,
  includeArchived,
  setClearColumnFiltersHandler,
  setExportCsvHandler,
  fiSlug,
  userId,
}: FilterableDashboardProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const orgMatch = useRouteMatch("/organization/:id");
  const user = useSelector(selectMyUser);
  const { currentSubmissions, currentTotalRecords } =
    useSelector(selectApplications);

  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= MOBILE_BREAKPOINT
  );

  // Refs to track previous filter values (to detect changes vs. initial mount)
  const prevSearchRef = useRef<string>(search);
  const prevActiveFilterRef = useRef(activeFilter);
  const prevIncludeArchivedRef = useRef(includeArchived);
  const prevGridFilterRef = useRef<any>(null);
  const prevGridSortRef = useRef<any>(null);

  // Parse page number from query string (1-based in URL, convert to 0-based for ag-Grid)
  const queryParams = new URLSearchParams(location.search);
  const pageFromUrl = queryParams.has("page")
    ? Math.max(0, parseInt(queryParams.get("page") || "1", 10) - 1)
    : null;

  // Create LocalStorage instances with user-specific baseKey (fi-slug/user-id)
  const { gridPreferences, gridContext } = useMemo(() => {
    const baseKey = fiSlug && userId ? `${fiSlug}/${userId}` : "shared";
    return {
      gridPreferences: new LocalStorage(baseKey, false), // localStorage
      gridContext: new LocalStorage(baseKey, true), // sessionStorage
    };
  }, [fiSlug, userId]);

  // Prepare initial state by merging localStorage (preferences) and sessionStorage (context)
  const initialState = useMemo(() => {
    const preferencesString = gridPreferences.getItem(
      "applications_gridPreferences"
    );
    const contextString = gridContext.getItem("applications_gridContext");

    let preferences: any = {};
    let context: any = {};

    // Parse preferences from localStorage
    if (preferencesString) {
      try {
        preferences = JSON.parse(preferencesString);
      } catch (error) {
        console.error("Failed to parse grid preferences:", error);
      }
    }

    // Parse context from sessionStorage
    if (contextString) {
      try {
        context = JSON.parse(contextString);
      } catch (error) {
        console.error("Failed to parse grid context:", error);
      }
    }

    // Merge preferences and context
    // Special handling for pagination: merge pageSize from preferences with page from context
    const mergedState = { ...preferences, ...context };
    if (preferences.pagination || context.pagination) {
      mergedState.pagination = {
        ...preferences.pagination,
        ...context.pagination,
      };
    }

    // Make sure a column order saved before the booking error column existed
    // still places it at the far left
    if (mergedState.columnOrder?.orderedColIds) {
      mergedState.columnOrder = {
        ...mergedState.columnOrder,
        orderedColIds: withBookingErrorColumn(
          mergedState.columnOrder.orderedColIds
        ),
      };
    }

    // Override page number with query string if present (source of truth for page)
    if (pageFromUrl !== null) {
      mergedState.pagination = {
        ...mergedState.pagination,
        page: pageFromUrl,
      };
    }

    // If no state at all, set default sort by createdAt descending
    if (Object.keys(mergedState).length === 0) {
      return {
        sort: {
          sortModel: [{ colId: "createdAt", sort: "desc" }],
        },
      };
    }

    // If we have state but no sort, add default sort
    if (!mergedState.sort) {
      mergedState.sort = {
        sortModel: [{ colId: "createdAt", sort: "desc" }],
      };
    }

    return mergedState;
  }, [gridPreferences, gridContext, pageFromUrl]); // Re-compute when storage instances or page changes

  // Helper function to extract state into preferences and context objects
  const extractGridState = useCallback((state: any) => {
    // Extract preferences for localStorage
    const preferences: any = {};
    if (state.columnOrder) preferences.columnOrder = state.columnOrder;
    if (state.columnSizing) preferences.columnSizing = state.columnSizing;
    if (state.pagination?.pageSize !== undefined) {
      preferences.pagination = { pageSize: state.pagination.pageSize };
    }

    // Extract context for sessionStorage (excluding page - that goes to URL)
    const context: any = {};
    if (state.filter) context.filter = state.filter;
    if (state.sort) context.sort = state.sort;

    return { preferences, context };
  }, []);

  // Helper function to reset pagination to page 1
  const resetToPageOne = useCallback(() => {
    // Update URL - remove page param or set to 1
    const newParams = new URLSearchParams(location.search);
    if (newParams.has("page")) {
      newParams.delete("page"); // Remove page param to default to page 1
      history.replace({
        pathname: location.pathname,
        search: newParams.toString(),
      });
    }

    // Reset grid to page 1
    if (gridApi) {
      gridApi.paginationGoToPage(0);
    }
  }, [gridApi, history, location.pathname, location.search]);

  const fetchApplications = useCallback(() => {
    return dispatch(
      getApplicationsCurrent({
        limit: 15000,
        assignedToMe: activeFilter.assignedToMe,
        decisionStatus: activeFilter.decisionStatus,
        includeArchived,
        dashboardFilter,
      })
    );
  }, [dispatch, activeFilter, includeArchived, dashboardFilter]);

  useEffect(() => {
    // Only fetch if we have the required user and org info
    if (fiSlug && userId) {
      fetchApplications();
    }
  }, [fetchApplications, fiSlug, userId]);

  const [isLoading] = useRequestStatus(getApplicationsCurrent.typePrefix);

  const getAmountRequested = (amountRequested: number | string): string => {
    if (!amountRequested) return "--------";
    // Parse if it's a string
    const numValue =
      typeof amountRequested === "string"
        ? parseFloat(amountRequested)
        : amountRequested;
    if (!numValue) return "--------";
    const formattedRequest = formatMoney(numValue);
    if (formattedRequest === "$0.00") return "--------";
    return formattedRequest || "--------";
  };

  // Helper function to check for recent activity
  const hasRecentActivity = (lastActivity: string, lastViewed?: string) => {
    if (!lastActivity) return false;
    if (!lastViewed) return true;

    // Previous grid had a bug in the logic that follows, where it would always
    // evaluate to false. Preserving the behavior users are accustomed to.
    return false;

    // const lastActivityDate = new Date(lastActivity);
    // const lastViewedDate = new Date(lastViewed);
    // return lastActivityDate > lastViewedDate;
  };

  // Column Definitions - using width instead of flex for themeQuartz compatibility
  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: BOOKING_ERROR_COL_ID,
        headerName: "",
        // The header is blank, so give it an explanation on header hover too
        headerTooltip: "Booking error",
        // Wider than the neighbouring indicator columns: a 14px glyph needs
        // more room than a text dot to sit centered without clipping.
        // suppressSizeToFit keeps that width - sizeColumnsToFit() otherwise
        // redistributes it across every column that does not opt out.
        width: 32,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: BookingErrorRenderer,
        cellStyle: {
          color: BOOKING_ERROR_RED,
          textAlign: "center",
          fontSize: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 1,
        },
        // Drives CSV export and quick-filter matching, not the cell display.
        // ag-grid calls this with data: undefined for rows still loading.
        valueGetter: (params: ValueGetterParams) => {
          return params.data?.hasBookingError ? "Booking Error" : "";
        },
      },
      {
        field: "activity",
        headerName: "",
        width: 20,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: (params: ICellRendererParams) => {
          if (
            hasRecentActivity(params.data.lastActivity, params.data.lastViewed)
          ) {
            return "●";
          }
          return "";
        },
        cellStyle: {
          color: "#52c1b2",
          textAlign: "center",
          fontSize: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 1,
        },
        valueGetter: (params: ValueGetterParams) => {
          return hasRecentActivity(
            params.data.lastActivity,
            params.data.lastViewed
          )
            ? "Unread"
            : "";
        },
      },
      {
        field: "isArchived",
        headerName: "",
        width: 20,
        sortable: false,
        filter: false,
        resizable: false,
        hide: !includeArchived,
        cellRenderer: ArchiveIconRenderer,
        cellStyle: {
          color: "#BAC9C7",
          textAlign: "center",
          fontSize: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          lineHeight: 1,
        },
        valueGetter: (params: ValueGetterParams) => {
          return params.data.isArchived ? "Archived" : "";
        },
      },
      {
        field: "id",
        headerName: "Application #",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        width: 140,
      },
      {
        field: "assignedTo",
        headerName: "Assigned To",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        width: 150,
        valueGetter: (params: ValueGetterParams) => {
          const firstName = params.data.assignedToFirstName || "";
          const lastName = params.data.assignedToLastName || "";
          return `${firstName} ${lastName}`.trim() || "-";
        },
      },
      {
        field: "applicantFirstName",
        headerName: "First Name",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        width: 120,
        valueFormatter: (params) => capitalizeFirstLetter(params.value || ""),
      },
      {
        field: "applicantLastName",
        headerName: "Last Name",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        width: 120,
        valueFormatter: (params) => capitalizeFirstLetter(params.value || ""),
      },
      {
        field: "flowTitle",
        headerName: "Workflow",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        width: 200,
        valueFormatter: (params) => capitalizeFirstLetter(params.value || ""),
      },
      {
        headerName: "Request Amount",
        filter: "agNumberColumnFilter",
        sortable: true,
        resizable: true,
        width: 150,
        valueGetter: (params) => {
          // Parse string value to number for filtering and sorting
          const value = params.data?.amountRequested;
          if (
            value === null ||
            value === undefined ||
            value === "" ||
            value === "0" ||
            value === "0.00"
          ) {
            return null;
          }
          // Convert string to number
          const numValue =
            typeof value === "string" ? parseFloat(value) : value;
          return numValue || null;
        },
        valueFormatter: (params) => getAmountRequested(params.value),
      },
      {
        field: "createdAt",
        headerName: "Received",
        filter: "agDateColumnFilter",
        sortable: true,
        resizable: true,
        width: 130,
        valueFormatter: (params) => formatDate(params.value) || "",
        filterParams: {
          includeTime: false,
        },
      },
      {
        field: "lastActivity",
        headerName: "Last Activity",
        filter: "agDateColumnFilter",
        sortable: true,
        resizable: true,
        width: 140,
        valueFormatter: (params) => formatDate(params.value) || "N/A",
        filterParams: {
          includeTime: false,
        },
      },
      {
        field: "decisionStatusTitle",
        headerName: "Status",
        filter: "agTextColumnFilter",
        sortable: true,
        resizable: true,
        width: 180,
        valueFormatter: (params) => params.value || "-",
      },
    ],
    [includeArchived]
  );

  // Default column configuration
  const defaultColDef: ColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      suppressMovable: false, // Enable column dragging/reordering
    }),
    []
  );

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    // Only fit columns on desktop viewports
    if (!isMobile) {
      params.api.sizeColumnsToFit();
    }
  };

  const onFirstDataRendered = (params: FirstDataRenderedEvent) => {
    // Only fit columns on desktop when data is first rendered
    if (!isMobile) {
      params.api.sizeColumnsToFit();
    }
  };

  // Handle window resize to track mobile state and refit columns (desktop only)
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);

      if (gridApi && !mobile) {
        gridApi.sizeColumnsToFit();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [gridApi]);

  const onRowClicked = (event: RowClickedEvent) => {
    history.push(`${orgMatch?.url}/applications/${event.data.id}/activity`);
  };

  // Function to clear column filters - exposed to parent via setClearColumnFiltersHandler
  const clearColumnFilters = () => {
    if (gridApi) {
      gridApi.setFilterModel(null);
      // Clear sessionStorage context only (filters, sort, page)
      // Keep localStorage preferences (column layout, page size)
      gridContext.removeItem("applications_gridContext");
    }
  };

  // Function to export CSV - exposed to parent via setExportCsvHandler
  const exportCsv = () => {
    if (gridApi) {
      const now = new Date();
      const timestamp = now
        .toISOString()
        .replace(/T/, "_")
        .replace(/:/g, "-")
        .split(".")[0]; // Format: YYYY-MM-DD_HH-MM-SS
      gridApi.exportDataAsCsv({
        fileName: `applications-export-${timestamp}.csv`,
        // The booking error column renders an icon under a blank header, but
        // the CSV is read by people - give it a real header there. Everything
        // else keeps the header ag-grid would have used.
        processHeaderCallback: (params) =>
          params.column.getColId() === BOOKING_ERROR_COL_ID
            ? "Booking Error"
            : params.api.getDisplayNameForColumn(params.column, null),
        // Export all columns except the activity indicator
        columnKeys: [
          BOOKING_ERROR_COL_ID,
          "id",
          "isArchived",
          "assignedTo",
          "applicantFirstName",
          "applicantLastName",
          "flowTitle",
          "amountRequested",
          "createdAt",
          "lastActivity",
          "decisionStatusTitle",
        ],
      });
    }
  };

  // Register handlers with parent on mount and when gridApi changes
  useEffect(() => {
    setClearColumnFiltersHandler(() => clearColumnFilters);
    setExportCsvHandler(() => exportCsv);
  }, [gridApi, setClearColumnFiltersHandler, setExportCsvHandler]);

  // Save grid state whenever it changes using the stateUpdated event
  // Split state into preferences (localStorage) and context (sessionStorage)
  const onStateUpdated = useCallback(
    (event: StateUpdatedEvent) => {
      const state = event.state;

      // Check if column filters or sort changed (not just initial state)
      const filterChanged =
        prevGridFilterRef.current !== null &&
        JSON.stringify(state.filter) !==
          JSON.stringify(prevGridFilterRef.current);
      const sortChanged =
        prevGridSortRef.current !== null &&
        JSON.stringify(state.sort) !== JSON.stringify(prevGridSortRef.current);

      // If filters or sort changed, reset to page 1
      if (filterChanged || sortChanged) {
        prevGridFilterRef.current = state.filter;
        prevGridSortRef.current = state.sort;
        resetToPageOne();
        return; // Don't update URL with old page, resetToPageOne handles it
      }

      // Store current state for next comparison
      if (prevGridFilterRef.current === null) {
        prevGridFilterRef.current = state.filter;
      }
      if (prevGridSortRef.current === null) {
        prevGridSortRef.current = state.sort;
      }

      // Extract preferences and context from state
      const { preferences, context } = extractGridState(state);

      // Update URL with current page number (1-based for URL)
      if (state.pagination?.page !== undefined) {
        const newPage = state.pagination.page + 1; // Convert to 1-based
        const currentParams = new URLSearchParams(location.search);
        const currentPageFromUrl = currentParams.get("page");
        const currentPage = currentPageFromUrl
          ? parseInt(currentPageFromUrl, 10)
          : 1;

        if (newPage !== currentPage) {
          const newParams = new URLSearchParams(location.search);
          newParams.set("page", newPage.toString());
          history.replace({
            pathname: location.pathname,
            search: newParams.toString(),
          });
        }
      }

      // Save to respective storages
      gridPreferences.setItem(
        "applications_gridPreferences",
        JSON.stringify(preferences)
      );
      gridContext.setItem("applications_gridContext", JSON.stringify(context));
    },
    [
      gridPreferences,
      gridContext,
      history,
      location.pathname,
      location.search,
      resetToPageOne,
      extractGridState,
    ]
  );

  // Also save on unmount as a backup
  useEffect(() => {
    return () => {
      if (gridApi) {
        try {
          const state = gridApi.getState();
          if (!state) return; // Grid already destroyed

          // Extract preferences and context from state
          const { preferences, context } = extractGridState(state);

          // Save to respective storages
          gridPreferences.setItem(
            "applications_gridPreferences",
            JSON.stringify(preferences)
          );
          gridContext.setItem(
            "applications_gridContext",
            JSON.stringify(context)
          );
        } catch (error) {
          // Grid was already destroyed, ignore
          console.debug(
            "Grid state not saved on unmount - grid already destroyed"
          );
        }
      }
    };
  }, [gridApi, gridPreferences, gridContext, extractGridState]);

  // Handle search - reset to page 1 when search changes
  useEffect(() => {
    if (gridApi) {
      gridApi.setGridOption("quickFilterText", search);

      // Reset to page 1 if search actually changed (not initial mount)
      if (prevSearchRef.current !== search) {
        resetToPageOne();
      }
      prevSearchRef.current = search;
    }
  }, [search, gridApi, resetToPageOne]);

  // Reset to page 1 when activeFilter changes (view dropdown)
  useEffect(() => {
    if (gridApi) {
      const prevFilter = prevActiveFilterRef.current;
      const filterChanged =
        prevFilter.assignedToMe !== activeFilter.assignedToMe ||
        prevFilter.decisionStatus !== activeFilter.decisionStatus;

      if (filterChanged) {
        resetToPageOne();
      }
      prevActiveFilterRef.current = activeFilter;
    }
  }, [activeFilter, gridApi, resetToPageOne]);

  // Reset to page 1 when includeArchived changes
  useEffect(() => {
    if (gridApi) {
      if (prevIncludeArchivedRef.current !== includeArchived) {
        resetToPageOne();
      }
      prevIncludeArchivedRef.current = includeArchived;
    }
  }, [includeArchived, gridApi, resetToPageOne]);

  if (!user) {
    return null;
  }

  // Check if we hit the 15000 record limit - only show when not loading
  const showLimitWarning = !isLoading && currentTotalRecords >= 15000;

  return (
    <>
      <GlobalFlashMessage />

      {/* Warning banner when hitting the 15000 record limit */}
      {showLimitWarning && (
        <Alert
          message={
            <span>
              <strong>Showing 15,000 applications.</strong> To see all records:
              filter using View dropdown
              {includeArchived ? ", uncheck 'Include Archived'," : ""} or{" "}
              <a
                href="?view=classic"
                style={{ textDecoration: "underline", color: "inherit" }}
              >
                switch to Classic View
              </a>{" "}
              for unlimited pagination.
            </span>
          }
          type="warning"
          banner
          closable
          style={{ marginBottom: "10px" }}
        />
      )}

      <div
        className="application-table"
        style={{
          height: showLimitWarning
            ? "calc(100vh - 230px)"
            : "calc(100vh - 180px)",
          width: "100%",
          minWidth: isMobile ? `${MOBILE_GRID_MIN_WIDTH}px` : undefined,
        }}
      >
        <Spinner show={isLoading} />

        {!isLoading && currentSubmissions.length > 0 ? (
          <AgGridReact
            ref={gridRef}
            rowData={currentSubmissions}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={50}
            paginationPageSizeSelector={[25, 50, 100, 200]}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            onRowClicked={onRowClicked}
            onStateUpdated={onStateUpdated}
            animateRows={true}
            suppressCellFocus={true}
            theme={customTheme}
            suppressDragLeaveHidesColumns={true}
            suppressMovableColumns={false}
            rowStyle={{ cursor: "pointer" }}
            initialState={initialState}
          />
        ) : (
          !isLoading && (
            <p className="d-block text-center mt-5 h5 font-italic text-muted">
              No entries found
            </p>
          )
        )}
      </div>
    </>
  );
};
